import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  output,
  signal,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { WhiteLabelService } from '@flexyoo/common';
import { PopUpService } from '@flexyoo/shared/ui';
import { UsersDataAccessService } from '@flexyoo/user/data-access';
import { UserSettingsActions } from '@flexyoo/user/store';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { catchError, EMPTY, finalize } from 'rxjs';
import { MobilityBudgetPhoneInputComponent } from '../mobility-budget-phone-input';
import {
  formatPhoneNumberForDisplay,
  formatPhoneNumberNationalForDisplay,
} from '../mobility-budget-phone-input/utils';

@Component({
  selector: 'app-mobility-budget-phone-step',
  templateUrl: './mobility-budget-phone-step.component.html',
  styleUrl: './mobility-budget-phone-step.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [TranslateModule, MatButtonModule, MatIcon, MobilityBudgetPhoneInputComponent],
})
export class MobilityBudgetPhoneStepComponent {
  private readonly translate = inject(TranslateService);
  private readonly popUpService = inject(PopUpService);
  private readonly usersDataAccessService = inject(UsersDataAccessService);
  private readonly store = inject(Store);
  protected readonly wls = inject(WhiteLabelService);
  protected readonly phoneInput = viewChild(MobilityBudgetPhoneInputComponent);

  readonly savedPhoneNumber = input<string | null>(null);
  readonly confirmed = output<string>();

  protected readonly isConfirmed = signal(false);
  protected readonly isSaving = signal(false);

  protected onSavedPhoneParsed(): void {
    this.isConfirmed.set(true);
  }

  protected confirmPhone(): void {
    if (this.isConfirmed() || this.isSaving()) {
      return;
    }

    const phoneInput = this.phoneInput();
    const e164 = phoneInput?.getE164() ?? null;

    if (!e164) {
      return;
    }

    const internationalDisplayPhone = formatPhoneNumberForDisplay(e164);
    const nationalDisplayPhone = formatPhoneNumberNationalForDisplay(e164);

    this.popUpService
      .openConfirmDialog({
        title: 'user-agreement-sign-page.mobility-budget.phone-step.confirm-modal.title',
        text: this.translate.instant(
          'user-agreement-sign-page.mobility-budget.phone-step.confirm-modal.text',
          { phoneNumber: internationalDisplayPhone }
        ),
        textYes: 'general.conf',
        textNo: 'general.cancel',
        width: '500px',
      })
      .subscribe((isConfirmed) => {
        if (isConfirmed) {
          this.persistPhoneNumber(e164, nationalDisplayPhone);
        }
      });
  }

  private persistPhoneNumber(e164: string, displayPhone: string): void {
    this.isSaving.set(true);

    this.usersDataAccessService
      .updateMobilePhoneNumber(e164)
      .pipe(
        catchError(() => {
          this.popUpService.openInfoDialog({
            title: 'general.attention',
            text: 'user-agreement-sign-page.mobility-budget.phone-step.save-error',
          });

          return EMPTY;
        }),
        finalize(() => this.isSaving.set(false))
      )
      .subscribe(() => {
        this.isConfirmed.set(true);
        this.phoneInput()?.setNationalDisplayValue(displayPhone);
        this.store.dispatch(UserSettingsActions.load());
        this.confirmed.emit(e164);
      });
  }
}
